import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const PROVIDERS = [
  { v: "openai", l: "OpenAI" },
  { v: "anthropic", l: "Anthropic (Claude)" },
  { v: "gemini", l: "Google Gemini" },
  { v: "ollama", l: "Ollama self-hosted" },
];

export default function AISettings() {
  const [cfg, setCfg] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => { api.get("/ai/config").then(({ data }) => setCfg(data)); }, []);

  const upd = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    try {
      const { data } = await api.put("/ai/config", cfg);
      setCfg(data); toast.success("Configurazione salvata");
    } catch (e) { toast.error("Errore"); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/ai/test-connection");
      data.ok ? toast.success(`Connessione OK · ${data.response.slice(0, 80)}`) : toast.error(data.response);
    } catch (e) { toast.error("Errore"); } finally { setTesting(false); }
  };

  if (!cfg) return <div>Caricamento…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="ai-page">
      <div>
        <div className="label-tag">Motore AI</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-1">Configurazione AI</h1>
        <p className="text-slate-600 text-sm mt-2">Configura il provider LLM per analisi rischi, controlli e interpelli. Compatibile OpenAI · Anthropic · Gemini · Ollama.</p>
      </div>

      <Card className="card-flat p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="label-tag">Provider</Label>
            <Select value={cfg.provider} onValueChange={(v) => upd("provider", v)}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="ai-provider"><SelectValue /></SelectTrigger>
              <SelectContent>{PROVIDERS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="label-tag">Modello</Label><Input value={cfg.model} onChange={(e) => upd("model", e.target.value)} className="rounded-sm mt-1" data-testid="ai-model" /></div>
          <div><Label className="label-tag">Base URL (opzionale)</Label><Input value={cfg.base_url || ""} onChange={(e) => upd("base_url", e.target.value)} placeholder="https://api.openai.com/v1" className="rounded-sm mt-1" /></div>
          <div><Label className="label-tag">API Key</Label><Input type="password" value={cfg.api_key || ""} onChange={(e) => upd("api_key", e.target.value)} placeholder="sk-…" className="rounded-sm mt-1" data-testid="ai-key" /></div>
          <div>
            <Label className="label-tag">Max tokens</Label>
            <Input type="number" value={cfg.max_tokens} onChange={(e) => upd("max_tokens", parseInt(e.target.value))} className="rounded-sm mt-1" />
          </div>
          <div>
            <Label className="label-tag">Temperature ({cfg.temperature})</Label>
            <Slider min={0} max={1} step={0.05} value={[cfg.temperature]} onValueChange={(v) => upd("temperature", v[0])} className="mt-3" />
          </div>
        </div>

        <div className="space-y-3 pt-4 border-t border-slate-200">
          <label className="flex items-center justify-between">
            <span className="text-sm text-slate-700">Usa la Emergent Universal Key (consigliato)</span>
            <Switch checked={!!cfg.use_emergent_key} onCheckedChange={(v) => upd("use_emergent_key", v)} data-testid="ai-emergent-key" />
          </label>
          <label className="flex items-center justify-between">
            <span className="text-sm text-slate-700">Scoring automatico rischi</span>
            <Switch checked={!!cfg.auto_scoring_enabled} onCheckedChange={(v) => upd("auto_scoring_enabled", v)} />
          </label>
          <label className="flex items-center justify-between">
            <span className="text-sm text-slate-700">Suggerimenti controlli</span>
            <Switch checked={!!cfg.suggestion_enabled} onCheckedChange={(v) => upd("suggestion_enabled", v)} />
          </label>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          <div className="text-xs text-slate-500">Chiamate AI questo mese: <span className="data-num font-medium text-slate-900">{cfg.monthly_calls}</span> / {cfg.monthly_limit}</div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={test} disabled={testing} className="rounded-sm" data-testid="ai-test">{testing ? "Test…" : "Test connessione"}</Button>
            <Button onClick={save} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="ai-save">Salva configurazione</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
